import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import changePickUpWarehouse from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/ChangePickUpWarehouse";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import WarehouseAddressInfoDisplay, {
    WarehouseAddressInfoDisplayStyles,
} from "@insite/content-library/Components/WarehouseAddressInfoDisplay";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FindLocationModal, { FindLocationModalStyles } from "@thos/Overrides/Components/FindLocationModal";
import CustomApplicationState from "@thos/Store/CustomApplicationState";
import getCustomerWarehouse from "@thos/Store/Data/CustomerWarehouse/Handlers/GetCustomerWarehouse";
import setLoadedShipFromWarehouse from "@thos/Store/Data/ShipFromWarehouse/Handlers/Header/setLoadedShipFromWarehouse";
import setSelectedShipFromWarehouse from "@thos/Store/Data/ShipFromWarehouse/Handlers/Header/setSelectedShipFromWarehouse";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps { }

const mapStateToProps = (state: CustomApplicationState) => ({
    session: state.context.session,
    fulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
    pickUpWarehouse: state.components.addressDrawer.pickUpWarehouse,
    selectedBillTo: state.components.addressDrawer.selectedBillTo,
    selectedShipTo: state.components.addressDrawer.selectedShipTo,
    selectedShipFromWarehouse: state.ThosState.shipFromWarehouseState.selectedShipFromWarehouse,
    currentShipFromWarehouse: state.ThosState.shipFromWarehouseState.currentShipFromWarehouse,
    loadedShipFromWarehouse: state.ThosState.shipFromWarehouseState.loadedShipFromWarehouse
});

const mapDispatchToProps = {
    loadCurrentCart,
    setFulfillmentMethod,
    changePickUpWarehouse,
    getCustomerWarehouse,
    setLoadedShipFromWarehouse,
    setSelectedShipFromWarehouse,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddressDrawerPickUpLocationSelectorStyles {
    wrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    changeLink?: LinkPresentationProps;
    warehouseAddress?: WarehouseAddressInfoDisplayStyles;
    findLocationModal?: FindLocationModalStyles;
}

export const addressDrawerPickUpLocationSelectorStyles: AddressDrawerPickUpLocationSelectorStyles = {
    headerText: {
        weight: 600,
    },
    changeLink: {
        css: css`
            margin-left: 15px;
        `,
    },
};

const styles = addressDrawerPickUpLocationSelectorStyles;

const AddressDrawerPickUpLocationSelector = ({
    session,
    fulfillmentMethod,
    pickUpWarehouse,
    changePickUpWarehouse,
    setFulfillmentMethod,
    selectedShipTo,
    selectedBillTo,
    getCustomerWarehouse,
    selectedShipFromWarehouse,
    currentShipFromWarehouse,
    loadedShipFromWarehouse,
    setLoadedShipFromWarehouse,
    setSelectedShipFromWarehouse
}: Props) => {
    useEffect(() => {
        if (selectedBillTo !== undefined && fulfillmentMethod === FulfillmentMethod.Ship) {
            getCustomerWarehouse({
                billTo: selectedBillTo?.customerNumber ?? "",
                shipTo: selectedShipTo?.customerSequence ?? "",
                onSuccess: (warehouse) => {
                    if (warehouse) {
                        setLoadedShipFromWarehouse({ loadedShipFromWarehouse: warehouse });
                    }
                }
            })
        }
    }, [selectedBillTo, selectedShipTo])

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const handleChangeLocationClick = () => {
        setIsModalOpen(true);
    };
    const handleModalClose = () => setIsModalOpen(false);
    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        changePickUpWarehouse({ warehouse });
        setIsModalOpen(false);
    };
    const handleShipFromWarehouseSelected = (warehouse: WarehouseModel) => {
        setSelectedShipFromWarehouse({ selectedShipFromWarehouse: warehouse });
        setIsModalOpen(false);
    };
    const showWarehouse = (warehouse: WarehouseModel) => {
        return (
            <WarehouseAddressInfoDisplay
                warehouse={warehouse}
                extendedStyles={styles.warehouseAddress}
            />
        )
    }

    return (
        <>
            <StyledWrapper
                id="addressDrawerPickUpLocationSelector"
                role="region"
                aria-live="polite"
                data-test-selector="changeCustomer_pickUpLocation"
                {...styles.wrapper}
            >
                {fulfillmentMethod === FulfillmentMethod.PickUp && (
                    <>
                        <Typography {...styles.headerText}>{translate("Pick Up Address")}</Typography>
                        <Link
                            onClick={handleChangeLocationClick}
                            data-test-selector="changeCustomer_pickUpLocation_findLocation"
                            {...styles.changeLink}
                        >
                            {translate("Find Location")}
                        </Link>
                        {pickUpWarehouse && (
                            <WarehouseAddressInfoDisplay
                                warehouse={pickUpWarehouse}
                                extendedStyles={styles.warehouseAddress}
                            />
                        )}
                        <FindLocationModal
                            modalIsOpen={isModalOpen}
                            onWarehouseSelected={handleWarehouseSelected}
                            onModalClose={handleModalClose}
                            extendedStyles={styles.findLocationModal}
                        />
                    </>
                )}
                {fulfillmentMethod === FulfillmentMethod.Ship && session.billToId && (
                    <>
                        <Typography {...styles.headerText}>{translate("Ship From Address")}</Typography>
                        <Link
                            onClick={handleChangeLocationClick}
                            data-test-selector="changeCustomer_pickUpLocation_findLocation"
                            {...styles.changeLink}
                        >
                            {translate("Find Location")}
                        </Link>

                        {(currentShipFromWarehouse)
                            && (selectedShipFromWarehouse ? showWarehouse(selectedShipFromWarehouse) :
                                loadedShipFromWarehouse ? showWarehouse(loadedShipFromWarehouse) :
                                    showWarehouse(currentShipFromWarehouse)
                            )
                        }
                        <FindLocationModal
                            overrideHeading={true}
                            headingOverrideText="Select Ship From Location"
                            modalIsOpen={isModalOpen}
                            onWarehouseSelected={handleShipFromWarehouseSelected}
                            onModalClose={handleModalClose}
                            extendedStyles={styles.findLocationModal}
                        />
                    </>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AddressDrawerPickUpLocationSelector),
    definition: {
        group: "Header",
        icon: "store",
    },
};

export default widgetModule;
